/**
 * A blacklist query builder to convert into a GQL JSON schema.
 */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { observable, action } from 'mobx';
import './Blacklist.css';
import './SelectBoxStyles.css';

import * as Select from 'react-select';
import * as _ from 'lodash';

import { PostStore } from '../../../stores/PostStore';
import { BlacklistInput } from './BlacklistInput';

export enum BlacklistDirective {
    BLOCK,
    UNLESS,
    AND,
}

export const directiveNames = {
    [BlacklistDirective.BLOCK]: 'block',
    [BlacklistDirective.UNLESS]: 'unless',
    [BlacklistDirective.AND]: 'and',
};

export interface BlacklistNode {
    id: string; // for react
    directive: BlacklistDirective;
    value: string;
    choices: BlacklistDirective[];
    shouldFocus?: boolean; // to automatically focus shortcut added inputs;
}

type ValidContexts = BlacklistDirective.BLOCK | BlacklistDirective.UNLESS;

function isContextToken(directive: BlacklistDirective): directive is ValidContexts {
    return directive === BlacklistDirective.BLOCK || directive === BlacklistDirective.UNLESS;
}

/**
 * Recursively parse a token to find valid tags for the next node.
 * 
 * @param {ValidContexts} context 
 * @param {BlacklistNode[]} tokens 
 * @returns {BlacklistDirective[]} 
 */
function parseDirectivePaths(
    context: ValidContexts,
    tokens: BlacklistNode[],
): BlacklistDirective[] {
    for (const token of tokens) {
        if (isContextToken(token.directive) && tokens.length > 1) {
            // skip the directive token so we dont get stuck in a recursive loop
            // #functional (even though it has a traditional loop :P)
            return parseDirectivePaths(token.directive, tokens.slice(tokens.indexOf(token) + 1));
        }
    }

    if (context === BlacklistDirective.UNLESS) {
        return [BlacklistDirective.AND, BlacklistDirective.BLOCK];
    }

    return [BlacklistDirective.AND];
}

/**
 * Find the next valid directive for a blacklist node set.
 * 
 * @param {BlacklistNode[]} nodeSet 
 * @returns {BlacklistDirective[]} 
 */
function getValidDirectives(nodeSet: BlacklistNode[]): BlacklistDirective[] {
    if (nodeSet.length <= 1) {
        return [BlacklistDirective.BLOCK, BlacklistDirective.UNLESS];
    }

    return parseDirectivePaths(nodeSet[0].directive as ValidContexts, nodeSet);
}

@inject(stores => ({
    postStore: stores.postStore as PostStore,
}))
@observer
export class Blacklist extends React.Component<{ postStore?: PostStore }> {
    // wut
    // giving a uniqueid to each blacklist node without breaking everything by changing the schema.
    tracers = this.props.postStore!.tracers;

    /**
     * Changes the input on the current node its set to.
     * 
     * @memberof Blacklist
     */
    @action
    inputChange = (node: number, nodeRef: number) => (
        newValue: string,
    ) => {
        this.props.postStore!.nodes[node][nodeRef].value = newValue;
        // this.setState({}); // wew hacky, observable doesn't update to force a rerender
    }

    /**
     * Changes what directive the node uses.
     * The selection list should be disabled for non active nodes (could result in a syntax error.)
     * 
     * @memberof Blacklist
     */
    @action
    selectionChange = (node: number, nodeRef: number) => (e: { value: number }) => {
        if (e == null) {
            return;
        }
        this.props.postStore!.nodes[node][nodeRef].directive = e.value;
    }

    /**
     * Creates a new node on the current active nodeset.
     * @param {number} node 
     * @memberof Blacklist
     */
    @action
    extendWorkingNode = (node: number, shouldFocus?: boolean) => () => {
        const newNode: BlacklistNode = {
            shouldFocus,
            id: _.uniqueId(),
            value: '',
            directive: 0,
            choices: getValidDirectives([
                ...this.props.postStore!.nodes[node],
                // push a fake node so the directive parser knows the full context.
                // don't push a real node, then edit it later, because the inputs will
                // change at random, resulting in missing directives, or directives that change
                {
                    id: _.uniqueId(),
                    value: '',
                    directive: _.last(this.props.postStore!.nodes[node])!.directive,
                    choices: [],
                },
            ]),
        };

        newNode.directive = newNode.choices[0];

        this.props.postStore!.nodes[node].push(newNode);
    }

    /**
     * Creates a new nodeset.
     * 
     * @memberof Blacklist
     */
    @action
    addNewNode = (shouldFocus?: boolean) => () => {
        this.props.postStore!.nodes.push([
            observable({
                id: _.uniqueId(),
                value: '',
                directive: 0,
                choices: [BlacklistDirective.BLOCK, BlacklistDirective.UNLESS],
                shouldFocus,
            }),
        ]);
        this.tracers.push(_.uniqueId());
    }

    /**
     * Removes a nodeset.
     * If removing all nodes, insert a new blank node.
     * 
     * @memberof Blacklist
     */
    @action
    removeNode = (node: number) => () => {
        if (this.props.postStore!.nodes.length <= 1) {
            this.props.postStore!.nodes = [
                [
                    observable({
                        id: _.uniqueId(),
                        value: '',
                        directive: 0,
                        choices: [BlacklistDirective.BLOCK, BlacklistDirective.UNLESS],
                    }),
                ],
            ];
            this.tracers = [_.uniqueId()];
            return;
        }

        _.pullAt(this.tracers, node);
        _.pullAt(this.props.postStore!.nodes, node);
        return;
    }

    @action
    popNodeFromSet = (node: number, shouldFocus?: boolean) => () => {
        if (this.props.postStore!.nodes[node].length > 1) {
            this.props.postStore!.nodes[node].pop();

            const current = _.last(this.props.postStore!.nodes[node])!;
            current.shouldFocus = true;
        } else {
            this.removeNode(node)();
            const nodeSet = _.last(this.props.postStore!.nodes)!;
            const lastNode = _.last(nodeSet)!;
            lastNode.shouldFocus = true;
        }
    }

    @action
    cycleNodeDirectives = (node: BlacklistNode, up: boolean) => {
        const set = up
            ? node.choices.slice(0, node.choices.indexOf(node.directive))
            : node.choices.slice(node.choices.indexOf(node.directive) + 1);

        const directive = _.first(set);

        if (directive != null) {
            node.directive = directive;
        }
    }

    shorcutEvent = (nodeSet: number, node: number) => (
        event: React.KeyboardEvent<HTMLInputElement>,
    ): void => {
        // TODO: cleanup these things
        // make the enter shorcut jump to an existing node if its empty
        // maybe insert inbetween lines instead of appending
        
        switch (event.which) {
            /**
             * @event {Enter}
             * Adds a new nodeset
             */
            case 13: {
                this.addNewNode(true)();
                return;
            }
            /**
             * @event {Space}
             * Adds a new node to the current node set
             */
            case 32: {
                event.preventDefault();
                this.extendWorkingNode(nodeSet, true)();
                return;
            }
            /**
             * @event {Up Arrow}
             * Cycle current directive selection
             */
            case 38: {
                if (event.shiftKey) {
                    event.preventDefault();
                    this.cycleNodeDirectives(this.props.postStore!.nodes[nodeSet][node], true);
                }
                return;
            }
            /**
             * @event {Down Arrow}
             * Cycle current directive, down.
             */
            case 40: {
                if (event.shiftKey) {
                    event.preventDefault();
                    this.cycleNodeDirectives(this.props.postStore!.nodes[nodeSet][node], false);
                }
                return;
            }
            /**
             * @event {Backspace}
             * Deletes a node if the input is empty.
             */
            case 8: {
                if (_.last(this.props.postStore!.nodes[nodeSet])!.value === '') {
                    event.preventDefault();
                    this.popNodeFromSet(nodeSet, true)();
                }
                return;
            }
            default: {
                return;
            }
        }
    }

    render() {
        return (
            <div className="Blacklist">
                {this.props.postStore!.nodes.map((node, outerNodeRef) => (
                    <div key={this.tracers[outerNodeRef]} className="Blacklist-set">
                        <div className="Blacklist-node Theme-background">{this.createNode(node, outerNodeRef)}</div>
                    </div>
                ))}
            </div>
        );
    }

    getSuggestions = async (input: string) =>
        this.props.postStore!.flatTags.length >= 1 &&
        this.props.postStore!.flatTags.filter(t => t.toLowerCase().startsWith(input.toLowerCase()))

    /**
     * Renders an existing nodeset row, creating valid directives and additional options.
     * 
     * @private
     * @param {BlacklistNode[]} node 
     * @param {number} outerNodeRef 
     * @returns {JSX.Element[]} 
     * @memberof Blacklist
     */
    private createNode(node: BlacklistNode[], outerNodeRef: number): JSX.Element[] {
        const lastNode = outerNodeRef === this.props.postStore!.nodes.length - 1;

        return node.map((innerNode, innerNodeRef) => {
            const inactiveNode = node.length - 1 !== innerNodeRef;
            const activeNode = innerNodeRef === node.length - 1;

            return (
                <div key={innerNodeRef} className="Blacklist-inner-node">
                    <div className="Blacklist-inputs">
                        <Select
                            name="directive"
                            onChange={this.selectionChange(outerNodeRef, innerNodeRef)}
                            value={innerNode.directive}
                            options={this.getDirectiveOptions(
                                inactiveNode,
                                innerNode,
                                node,
                                outerNodeRef,
                            )}
                            className="Blacklist-select Theme-background"
                        />
                        <BlacklistInput
                            onChange={this.inputChange(outerNodeRef, innerNodeRef)}
                            onKeyDown={this.shorcutEvent(outerNodeRef, innerNodeRef)}
                            node={node}
                            innerNode={innerNode}
                            innerNodeRef={innerNodeRef}
                            tags={this.props.postStore!.flatTags}
                            savedInput={innerNode.value}
                        />
                        {this.createNodeActions(
                            node,
                            activeNode,
                            innerNodeRef,
                            outerNodeRef,
                            lastNode,
                        )}
                    </div>
                </div>
            );
        });
    }

    /**
     * Creates an option input for a list of node, 
     * getting the appropriate directives if the node is active.
     * 
     * @private
     * @param {boolean} inactiveNode 
     * @param {BlacklistNode} innerNode 
     * @param {BlacklistNode[]} node 
     * @param {number} outerNodeRef 
     * @returns 
     * @memberof Blacklist
     */
    @action
    private getDirectiveOptions(
        inactiveNode: boolean,
        innerNode: BlacklistNode,
        node: BlacklistNode[],
        outerNodeRef: number,
    ) {
        if (inactiveNode) {
            return [{ value: innerNode.directive, label: directiveNames[innerNode.directive] }];
        } else {
            return innerNode.choices.map(c => ({
                value: c,
                label: directiveNames[c],
            }));
        }
    }

    /**
     * Create appropriate action buttons depending if the node is 
     * a) the active node
     * b) the last nodeset in the list of nodesets
     * 
     * @private
     * @param {BlacklistNode[]} node 
     * @param {boolean} activeNode 
     * @param {number} innerNodeRef 
     * @param {number} outerNodeRef 
     * @param {boolean} lastNode 
     * @returns 
     * @memberof Blacklist
     */
    @action
    private createNodeActions(
        node: BlacklistNode[],
        activeNode: boolean,
        innerNodeRef: number,
        outerNodeRef: number,
        lastNode: boolean,
    ) {
        return (
            (node.length < 1 || activeNode) && (
                <span className="Blacklist-buttons Theme-buttons"> 
                    {node[innerNodeRef].value && (
                        <button
                            className="Theme-background"
                            onClick={this.extendWorkingNode(outerNodeRef)}
                        >
                            Extend
                        </button>
                    )}
                    {lastNode ? (
                        <button className="Theme-background" onClick={this.addNewNode()}>
                            Add
                        </button>
                    ) : (
                        <button
                            className="Theme-background Blacklist-buttons-delete"
                            onClick={this.removeNode(outerNodeRef)}
                        >
                            Remove
                        </button>
                    )}
                </span>
            )
        );
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/Inputs/Blacklist/Blacklist.tsx